import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ISpace } from "../../../models/Space";
import Typhography from "../../../shared/Typhography/Typhography";
import { TypeSpaceIcon } from "../../../shared/Icons/Icons";
import { useSimpleForm } from "../../../hooks/useSimpleForm";
import CustomTextInput from "../../../shared/CustomInput/CustomTextInput";
import CustomSelectInput from "../../../shared/CustomSelectInput/CustomSelectInput";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/ssr";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import { useDropzone } from "react-dropzone";
import { ITeam } from "../../../models/Team";
import { LEVELS_SPORT_OPTIONS } from "../../../constants/config";
import { addBaseUrl } from "../../../lib/DurationPicker/utils";
import GenericModalV2 from "../../../shared/GenericModal/GenericModal";
import { Dialog, Tabs } from "@chakra-ui/react";
import CustomTabsV3 from "../../../shared/CustomTabsV3/CustomTabsV3";
import { AthletesTab } from "./AthletesTab/AthletesTab";
import { CoachesTab } from "../CoachesTab/CoachesTab";
import { IAthlete, ICoach, IUser2 } from "../../../models/user copy";
import { USER_TYPES } from "../../../enums/user";
import style from "./AddTeamModal.module.scss";
import CustomCard from "../../../shared/Card/CustomCard";
import { CustomSection } from "../../../shared/CustomSection/CustomSecton";

const tabs = [
  { label: "Informazioni", value: "informations" },
  { label: "Preparatori Atletici", value: "coaches" },
  { label: "Atleti", value: "athletes" },
  { label: "Impostazioni", value: "settings" },
];
const MAX_SIZE = 24 * 1024 * 1024; // 24 MB in byte

export type IAddTeamModalForm = {
  name: string;
  level: string;
  coaches: ICoach[];
  athletes: IAthlete[];
};

export const AddTeamModal: FC<{
  onClose: () => void;
  onDelete: () => void;
  onConfirm: (
    form: any,
    originalImage?: string,
    logoImage?: { file: File; url: string } | string
  ) => void;
  currentSpace: ISpace<ITeam>;
  isOpen?: boolean | ITeam;
  isLoading: boolean;
}> = ({ onClose, isOpen, currentSpace, onConfirm, isLoading, onDelete }) => {
  const [currentTab, setCurrentTab] = useState("informations");
  const isEditMode = !!isOpen && typeof isOpen !== "boolean";
  const [logoImage, setLogoImage] = useState<
    { file: File; url: string } | string
  >();
  const originalImage = useRef<string>();
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      if ((logoImage as any)?.url) URL.revokeObjectURL((logoImage as any)?.url);

      const file = acceptedFiles[0];
      const url = URL.createObjectURL(file);
      setLogoImage({ file, url });
    }
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
    maxFiles: 1,
    maxSize: MAX_SIZE,
  });
  const { form, onInputChange, handleChange, setForm } =
    useSimpleForm<IAddTeamModalForm>({
      name: "",
      level: "",
      coaches: [],
      athletes: [],
    });

  const onSave = () => {
    onConfirm(form, originalImage.current, logoImage);
  };

  useEffect(() => {
    if (isOpen) {
      if (typeof isOpen !== "boolean") {
        const { coaches, athletes } = isOpen.users!.reduce(
          (acc, curr) => {
            if (curr.type === USER_TYPES.COACH) {
              acc.coaches.push(curr);
            }
            if (curr.type === USER_TYPES.ATHLETE) {
              acc.athletes.push(curr);
            }
            return acc;
          },
          { coaches: [] as ICoach[], athletes: [] as IAthlete[] }
        );

        setLogoImage(isOpen.image);
        originalImage.current = (isOpen as ITeam)?.image;
        setForm({
          name: isOpen.name,
          level: isOpen.level,
          coaches,
          athletes,
        });
      } else {
        originalImage.current = undefined;
      }
    } else {
      setCurrentTab("informations");
      setLogoImage(undefined);
      setForm({
        name: "",
        level: "",
        coaches: [],
        athletes: [],
      });
    }
  }, [isOpen]);

  const imagisEmpytLogo =
    !logoImage ||
    (typeof logoImage === "string" && !logoImage) ||
    (typeof logoImage !== "string" && !(logoImage as any)?.url);
  const renderFirstTab = () => {
    return (
      <div className="d-flex flex-column gap-2 mt-4">
        <div className="d-flex flex-column gap-2">
          <Typhography size="bold-14">Logo</Typhography>
          <div className="d-flex gap-2">
            <input {...getInputProps()} />
            {imagisEmpytLogo ? (
              <div className={style.dropZoneContainer} {...getRootProps()}>
                <UploadSimpleIcon className={style.uploadIcon} />
              </div>
            ) : (
              <div className={style.dropZoneContainer2}>
                <img
                  src={
                    typeof logoImage === "string"
                      ? addBaseUrl(1, logoImage)
                      : logoImage.url
                  }
                  className={style.logoImage}
                />
              </div>
            )}
            <div className="d-flex flex-column gap-1">
              <CustomButton onClick={open} color="outline">
                Carica immagine
              </CustomButton>
              {imagisEmpytLogo ? (
                <Typhography size="regular-12">
                  ( Formato .png, .jpg e .jpeg 24mb max )
                </Typhography>
              ) : (
                <CustomButton
                  color="outline"
                  onClick={() => {
                    setLogoImage(undefined);
                  }}
                >
                  Rimuovi
                </CustomButton>
              )}
            </div>
          </div>
        </div>
        <CustomTextInput
          value={form.name}
          onChange={onInputChange("name")}
          label="Nome squadra *"
        />
        <CustomSelectInput
          label="Livello *"
          value={form.level}
          onChange={(newValue) => {
            handleChange("level", newValue);
          }}
          options={LEVELS_SPORT_OPTIONS[currentSpace.type]}
        />
      </div>
    );
  };

  const athletesTab = useMemo(() => {
    return <AthletesTab />;
  }, []);
  return (
    <GenericModalV2
      isLoading={isLoading}
      size={"lg"}
      onClose={onClose}
      isOpen={!!isOpen}
      Header={
        <Dialog.Header style={{ padding: 0, paddingBottom: "12px" }}>
          <div className="d-flex flex-column">
            <div className="d-flex gap-1 align-items-center">
              <Typhography size="bold-24">
                {isEditMode ? "Modifica" : "Nuovo"} Team
              </Typhography>
              <div className={style.typeContainer}>
                <TypeSpaceIcon type={currentSpace?.type} />
                <Typhography size="medium-12">{currentSpace?.type}</Typhography>
              </div>
            </div>
          </div>
        </Dialog.Header>
      }
    >
      <div className="d-flex flex-column">
        <CustomTabsV3
          value={currentTab}
          lazyMount
          unmountOnExit
          tabs={isEditMode ? tabs : [tabs[0]]}
          onValueChange={(e) => setCurrentTab(e.value)}
        >
          <Tabs.Content value="informations">{renderFirstTab()}</Tabs.Content>
          {isEditMode && (
            <>
              <Tabs.Content value="coaches">
                <CoachesTab
                  space={currentSpace}
                  coaches={form.coaches}
                  type="coaches"
                  onChange={(value) => {
                    handleChange("coaches", value as unknown as ICoach[]);
                  }}
                />
              </Tabs.Content>
              <Tabs.Content value="athletes">
                <CoachesTab
                  space={currentSpace}
                  coaches={form.athletes}
                  type="athletes"
                  onChange={(value) => {
                    handleChange("athletes", value as unknown as IAthlete[]);
                  }}
                />
              </Tabs.Content>
              <Tabs.Content value="settings">
                <CustomSection>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex flex-column gap-2">
                      <Typhography size="bold-16">Danger Zone</Typhography>
                      <Typhography size="regular-14">
                        Cancella definitivamente questo team
                      </Typhography>
                    </div>
                    <CustomButton color="error" onClick={() => { onClose(); onDelete(); }}>
                      Cancella team
                    </CustomButton>
                  </div>
                </CustomSection>
              </Tabs.Content>
            </>
          )}
        </CustomTabsV3>
      </div>
      <div className="d-flex justify-content-end gap-2 mt-3">
        <CustomButton color="outline" disabled={isLoading} onClick={onClose}>
          Indietro
        </CustomButton>
        <CustomButton
          color="secondary"
          onClick={onSave}
          isLoading={isLoading}
          disabled={!form.name && !form.level}
        >
          Salva
        </CustomButton>
      </div>
    </GenericModalV2>
  );
};

export default AddTeamModal;
